import { CURRENT_EVENT_YEAR } from '~~/shared/utilities/schedule'

// A client-only record of sessions the attendee has actively rejected — either
// by removing one from My Schedule or thumbs-downing it in Find a Session
// (Asana 687632). The Schedule Builder ("Build My Schedule" / "Fill All Open
// Time Slots") and Find a Session ("Fill This Time Slot") pass this list to the
// server so a session you just dismissed isn't recommended straight back to
// you — the exact "it keeps recommending the thing I removed" complaint.
//
// Persisted in localStorage, scoped per contact + event year, so it survives
// reloads for the run of the event. Re-adding a session to My Schedule clears
// its dismissal (an explicit add is the user changing their mind). This is a
// deliberately client-side, best-effort store (no schema/behaviour change to
// the schedule itself) — if storage is blocked the in-memory list still works
// for the current session.
export const useDismissedSessions = () => {
    const { user } = useUserSession()
    // Shared across every caller (useUserApi dismiss/undismiss, useFindASession
    // and useScheduleBuilder reads) so they all see one list.
    const ids = useState('dismissed-sessions', () => [])
    // The storage key the current `ids` were loaded from — lets us re-hydrate if
    // the contact changes (e.g. the session cookie resolves after first call).
    const hydratedFor = useState('dismissed-sessions-hydrated-for', () => null)

    const key = () => `unbound-dismissed-${CURRENT_EVENT_YEAR}-${user.value?.contactId || 'anon'}`

    const hydrate = () => {
        if (!import.meta.client) return
        const k = key()
        if (hydratedFor.value === k) return
        try {
            const raw = localStorage.getItem(k)
            ids.value = raw ? JSON.parse(raw) : []
        } catch {
            ids.value = []
        }
        hydratedFor.value = k
    }

    const persist = () => {
        if (!import.meta.client) return
        try {
            localStorage.setItem(key(), JSON.stringify(ids.value))
        } catch {
            // Storage full/blocked (private mode, quota) — the in-memory list
            // still applies for this session; nothing to recover.
        }
    }

    const dismiss = (id) => {
        hydrate()
        if (!id || ids.value.includes(id)) return
        ids.value = [...ids.value, id]
        persist()
    }

    const undismiss = (id) => {
        hydrate()
        if (!id || !ids.value.includes(id)) return
        ids.value = ids.value.filter((x) => x !== id)
        persist()
    }

    // The current dismissed ids, hydrated from storage. Read at request time by
    // the builder/find-a-session callers to build the `exclude` param.
    const list = () => {
        hydrate()
        return ids.value
    }

    return { ids, dismiss, undismiss, list, hydrate }
}
